import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Header.css';

export default function Header({ theme, setTheme, currentPath }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const scrollTimerRef = React.useRef(null);
  const navigate = useNavigate();

  // Hide while scrolling, reappear 600ms after scroll stops
  useEffect(() => {
    const handleScroll = () => {
      // Only hide if user has scrolled down past the header height
      if (window.scrollY > 80) {
        setHeaderVisible(false);
      }
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => {
        setHeaderVisible(true);
      }, 600);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleNavClick = (path, sectionId) => {
    closeMobileMenu();
    if (sectionId) {
      if (window.location.pathname !== '/') {
        navigate(`/#${sectionId}`);
      } else {
        const el = document.getElementById(sectionId);
        if (el) {
          const promoEl = document.querySelector('.promo-banner, .announcement-bar');
          const promoH = promoEl ? Math.ceil(promoEl.getBoundingClientRect().height) : 0;
          const offset = promoH + 80 + 20;
          const elementPosition = el.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: elementPosition, offset,
            behavior: 'smooth'
          });
        }
      }
    } else {
      if (window.location.pathname === path) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate(path);
      }
    }
  };

  return (
    <header className={`header glass${headerVisible ? '' : ' header--hidden'}`}>
      <div className="header-container container">
        {/* Transparent Official Logo */}
        <Link to="/" onClick={() => handleNavClick('/')} className="logo-link">
          <img src="/logo.webp" alt="iLOVEthisgame Canada, Official Playermaker Partner Logo" className="logo-img" width="180" height="40" />
        </Link>

        {/* Desktop Navigation Links */}
        <nav className="desktop-nav">
          <Link to="/#how-it-works" onClick={() => handleNavClick('/', 'how-it-works')} className="nav-item">How It Works</Link>
          <Link to="/#features" onClick={() => handleNavClick('/', 'features')} className="nav-item">Technology</Link>
          <Link to="/bilateral-mastery" onClick={() => handleNavClick('/bilateral-mastery')} className={`nav-item ${currentPath === '/bilateral-mastery' ? 'active-link' : ''}`}>Research</Link>
          <Link to="/#product-showcase" onClick={() => handleNavClick('/', 'product-showcase')} className="nav-item">Shop</Link>
          <Link to="/#specs" onClick={() => handleNavClick('/', 'specs')} className="nav-item">Specs</Link>
          <Link to="/faq" onClick={() => handleNavClick('/faq')} className={`nav-item ${currentPath === '/faq' ? 'active-link' : ''}`}>FAQ &amp; Support</Link>
          <Link to="/blog" onClick={() => handleNavClick('/blog')} className={`nav-item ${currentPath && currentPath.startsWith('/blog') ? 'active-link' : ''}`}>Blog</Link>
          <Link to="/contact" onClick={() => handleNavClick('/contact')} className={`nav-item ${currentPath === '/contact' ? 'active-link' : ''}`}>Contact</Link>
          <Link to="/#product-showcase" onClick={() => handleNavClick('/', 'product-showcase')} className="btn-buy-nav">BUY NOW</Link>
        </nav>

        {/* Action icons & Theme segmented switch */}
        <div className="header-actions">
          {/* Segmented Theme Switcher (Desktop) */}
          <div className="theme-toggle-group">
            <button 
              onClick={() => setTheme('system')} 
              className={`theme-toggle-btn ${theme === 'system' ? 'active' : ''}`}
              title="Follow System Settings"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                <line x1="8" y1="21" x2="16" y2="21"></line>
                <line x1="12" y1="17" x2="12" y2="21"></line>
              </svg>
            </button>
            <button 
              onClick={() => setTheme('light')} 
              className={`theme-toggle-btn ${theme === 'light' ? 'active' : ''}`}
              title="Activate Light Mode"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5"></circle>
                <line x1="12" y1="1" x2="12" y2="3"></line>
                <line x1="12" y1="21" x2="12" y2="23"></line>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                <line x1="1" y1="12" x2="3" y2="12"></line>
                <line x1="21" y1="12" x2="23" y2="12"></line>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
              </svg>
            </button>
            <button 
              onClick={() => setTheme('dark')} 
              className={`theme-toggle-btn ${theme === 'dark' ? 'active' : ''}`}
              title="Activate Dark Mode"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
              </svg>
            </button>
          </div>

          {/* Quick 1-Tap Mobile Theme Toggle Button */}
          <button 
            onClick={() => {
              const nextTheme = theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark';
              setTheme(nextTheme);
            }} 
            className="icon-btn mobile-theme-icon-btn" 
            aria-label={`Toggle Theme (Current: ${theme})`}
            title={`Current Mode: ${theme}. Tap to switch.`}
          >
            {theme === 'light' ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5"></circle>
                <line x1="12" y1="1" x2="12" y2="3"></line>
                <line x1="12" y1="21" x2="12" y2="23"></line>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                <line x1="1" y1="12" x2="3" y2="12"></line>
                <line x1="21" y1="12" x2="23" y2="12"></line>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
              </svg>
            ) : theme === 'dark' ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                <line x1="8" y1="21" x2="16" y2="21"></line>
                <line x1="12" y1="17" x2="12" y2="21"></line>
              </svg>
            )}
          </button>

          {/* Quick Cart Shopping Bag Icon */}
          <Link to="/#product-showcase" onClick={() => handleNavClick('/', 'product-showcase')} className="header-icon-btn nav-cart-btn" aria-label="Shopping Cart">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
          </Link>

          {/* Mobile Menu Hamburger */}
          <button className="hamburger-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle Navigation Menu" aria-expanded={mobileMenuOpen}>
            <span className={`bar ${mobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`bar ${mobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`bar ${mobileMenuOpen ? 'open' : ''}`}></span>
          </button>
        </div>
      </div>

      {/* Mobile Navigation Dropdown */}
      {mobileMenuOpen && (
        <div className="mobile-nav glass animate-fade-in">
          <Link to="/#how-it-works" className="mobile-nav-item" onClick={() => handleNavClick('/', 'how-it-works')}>How It Works</Link>
          <Link to="/#features" className="mobile-nav-item" onClick={() => handleNavClick('/', 'features')}>Technology</Link>
          <Link to="/bilateral-mastery" className="mobile-nav-item" onClick={() => handleNavClick('/bilateral-mastery')}>Research Paper</Link>
          <Link to="/#product-showcase" className="mobile-nav-item" onClick={() => handleNavClick('/', 'product-showcase')}>Shop</Link>
          <Link to="/#specs" className="mobile-nav-item" onClick={() => handleNavClick('/', 'specs')}>Specs</Link>
          <Link to="/faq" className="mobile-nav-item" onClick={() => handleNavClick('/faq')}>FAQ &amp; Support Portal</Link>
          <Link to="/blog" className="mobile-nav-item" onClick={() => handleNavClick('/blog')}>Blog &amp; Insights</Link>
          <Link to="/contact" className="mobile-nav-item" onClick={() => handleNavClick('/contact')}>Contact Us</Link>
          <Link to="/#product-showcase" onClick={() => handleNavClick('/', 'product-showcase')} className="btn-buy-nav-mobile">BUY NOW</Link>
          
          {/* Theme switcher in mobile menu */}
          <div className="mobile-theme-switcher">
            <span className="mobile-theme-label">THEME</span>
            <div className="theme-toggle-group mobile-theme-group">
              <button onClick={() => { setTheme('system'); }} className={`theme-toggle-btn ${theme === 'system' ? 'active' : ''}`}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
              </button>
              <button onClick={() => { setTheme('light'); }} className={`theme-toggle-btn ${theme === 'light' ? 'active' : ''}`}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"></circle>
                  <line x1="12" y1="1" x2="12" y2="3"></line>
                  <line x1="12" y1="21" x2="12" y2="23"></line>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                  <line x1="1" y1="12" x2="3" y2="12"></line>
                  <line x1="21" y1="12" x2="23" y2="12"></line>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                </svg>
              </button>
              <button onClick={() => { setTheme('dark'); }} className={`theme-toggle-btn ${theme === 'dark' ? 'active' : ''}`}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
